import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { debug, isClient } from "@hornet-web-react/core/utils"
import type { MqttClient } from "mqtt"
import { propEq, uniq } from "ramda"
import type { IClientOptions } from "mqtt/src/lib/client"
import { useSessionUser } from "./session"
import { useCoreService } from "./services"
import LoggerService from "@hornet-web-react/core/services/LoggerService"
import { TYPES as CORE_TYPES } from "@hornet-web-react/core/services/types"
import { SessionPubSubConfig } from "@hornet-web-react/core/types/session"
import invariant from "tiny-invariant"

export interface WebsocketServiceContext {
  accessToken: string | null
  profileId: string | null
  host: string
}

export type ConnectionStatus =
  | "Disconnected"
  | "Connecting"
  | "Connected"
  | "Reconnecting"

export type MessageListenerCallback<CallbackMessageType> = (
  message: CallbackMessageType,
  context: WebsocketServiceContext
) => void

type MessageListener<CallbackMessageType> = {
  name: string
  topic: string
  callback: React.MutableRefObject<MessageListenerCallback<CallbackMessageType>>
}

interface WebSocketContextType {
  addMessageListener: <CallbackMessageType>(
    name: string,
    topic: string,
    callback: React.MutableRefObject<
      MessageListenerCallback<CallbackMessageType>
    >
  ) => void
  removeMessageListener: (name: string) => void
  connectionStatus: ConnectionStatus
}

const WebSocketContext = createContext<WebSocketContextType | null>(null)

export const WebSocketProvider: React.FC<{
  children: React.ReactNode
  appConfigMqttHost: string
  pubSubConfig?: SessionPubSubConfig
}> = ({ children, appConfigMqttHost, pubSubConfig }) => {
  const loggerService = useCoreService<LoggerService>(CORE_TYPES.LoggerService)
  const { currentUser } = useSessionUser()

  // track connection setup attempts (to keep the last one)
  const setupIdRef = useRef(0)
  const websocketRef = useRef<MqttClient | null>(null)

  const messageListeners = useRef<MessageListener<any>[]>([])
  const topicSubscriptionsQueue = useRef<string[]>([])
  const receivedMessageIds = useRef<Set<string>>(new Set())
  const [connectionStatus, setConnectionStatus] =
    useState<ConnectionStatus>("Disconnected")

  const logExceptionWithSentry = useMemo(
    () => loggerService.logExceptionWithSentry.bind(loggerService),
    [loggerService]
  )

  const createLoggingContext = useMemo(
    () => loggerService.createLoggingContext.bind(loggerService),
    [loggerService]
  )

  const context: WebsocketServiceContext = useMemo(() => {
    return {
      accessToken: pubSubConfig?.username ?? currentUser?.accessToken ?? null,
      profileId: pubSubConfig?.password ?? currentUser?.profileId ?? null,
      host:
        pubSubConfig?.wss_host && pubSubConfig?.wss_port
          ? `wss://${pubSubConfig.wss_host}:${pubSubConfig.wss_port}/mqtt`
          : appConfigMqttHost,
    }
  }, [currentUser, pubSubConfig, appConfigMqttHost])

  useEffect(() => {
    // do not even consider as initialized without the context values
    if (!isClient || !context.accessToken || !context.profileId) {
      return
    }

    let isCancelled = false
    const currentSetupId = ++setupIdRef.current

    const closeConnection = () => {
      if (websocketRef.current) {
        debug(`WebsocketService: closeConnection`)
        websocketRef.current.end(true)
        websocketRef.current = null
      }
    }

    const createConnection = async (reconnectPeriod = 1000) => {
      invariant(context.accessToken, "accessToken is missing")
      invariant(context.profileId, "profileId is missing")

      const mqtt = (await import("mqtt")).default

      // prevent older effects from continuing
      if (isCancelled || setupIdRef.current !== currentSetupId) return

      closeConnection()

      debug(`WebsocketService: createConnection`)

      const clientId =
        window.navigator.userAgent
          .substring(0, window.navigator.userAgent.indexOf(" "))
          .replace(/[.()]/g, "-")
          .toLowerCase() +
        "-" +
        context.profileId +
        Date.now()

      const clientOptions: IClientOptions = {
        username: String(context.accessToken),
        password: String(context.profileId),
        clientId,
        protocolId: "MQTT",
        protocolVersion: 4,
        clean: false,
        keepalive: 15,
        reconnectPeriod,
      }

      const client = mqtt.connect(context.host, clientOptions)
      websocketRef.current = client

      client.on("connect", () => {
        debug("WebsocketService: connected")
        setConnectionStatus("Connected")
      })

      client.on("disconnect", () => {
        debug("WebsocketService: disconnected")
        setConnectionStatus("Disconnected")
      })

      client.on("offline", () => {
        debug("WebsocketService: offline")
        setConnectionStatus("Disconnected")
      })

      let connectionErrorCounter = 0
      client.on("error", (error: Error | { code: number }) => {
        debug(`WebsocketService: error ${connectionErrorCounter}`)
        connectionErrorCounter++

        if (typeof error === "object" && "code" in error && error.code === 4) {
          setConnectionStatus("Disconnected")
        }

        // after 5 retries, let's increase the reconnect period
        if (connectionErrorCounter > 5) {
          client.end(true, () => {
            connectionErrorCounter = 0
            websocketRef.current = null
            createConnection(reconnectPeriod * 4)
          })
        }

        // log error only once in this session
        if (reconnectPeriod === 1000 && connectionErrorCounter === 5) {
          console.error(error)
        }
      })

      client.on("message", (topic, msg) => {
        const messageData = JSON.parse(msg.toString())

        try {
          // console.log(`topic, messageData`, topic, messageData)

          debug(`WebsocketService: message received on: ${topic}`)

          const msgId: string | undefined = messageData.pubsub_uuid

          if (msgId && receivedMessageIds.current.has(msgId)) {
            // duplicate, do not process
            debug(`WebsocketService: duplicate ignored: ${msgId}`)
            return
          }

          messageListeners.current.forEach((listener) => {
            if (!context.profileId) {
              throw new Error(
                "WebsocketService: on('message'): ProfileId is missing"
              )
            }

            const listenerTopicWithProfileId = listener.topic.replace(
              "{profileId}",
              context.profileId
            )

            if (listenerTopicWithProfileId === topic) {
              listener.callback.current(messageData, context)
            }
          })

          if (msgId) {
            receivedMessageIds.current.add(msgId)
          }
        } catch (error) {
          if (error instanceof Error) {
            // console.error(topic, msg.toString())

            logExceptionWithSentry(
              error,
              createLoggingContext({
                service: "WebsocketService",
                method: "client.on.message",
                messageType: messageData.data.message.type,
              })
            )
          }
        }
      })

      // if there are pending subscriptions, let's subscribe
      // to them and clear out the queue
      const queue = uniq(topicSubscriptionsQueue.current)
      if (queue.length > 0) {
        while (queue.length > 0) {
          const topic = queue.shift()
          topic &&
            client.subscribe(topic.replace("{profileId}", context.profileId), {
              qos: 1,
            })
        }
      }
    }

    createConnection().catch(console.error)

    return () => {
      isCancelled = true
      closeConnection()
    }
  }, [context, createLoggingContext, logExceptionWithSentry])

  const addMessageListener = useCallback(
    <CallbackMessageType,>(
      name: string,
      topic: string,
      callback: React.MutableRefObject<
        MessageListenerCallback<CallbackMessageType>
      >
    ) => {
      // only turn on when debugging listeners
      debug(`WebsocketService: addMessageListener(${name}, ${topic}, ...)`)

      // check so we don't add the same listener twice - we could also just override
      // the instance, but this way we can be more aware of what's going on
      if (messageListeners.current.some((listener) => listener.name === name)) {
        logExceptionWithSentry(
          new Error(`Message handler with name ${name} already exists`),
          createLoggingContext({
            service: "WebsocketService",
            method: "addMessageListener",
          })
        )
        return
      }

      messageListeners.current.push({ name, topic, callback })

      // subscribe to the topic if it's not already subscribed
      const isSubscribedAlready = messageListeners.current.some(
        (handler) => name === handler.topic
      )

      // subscribe if it's first topic listener
      if (!isSubscribedAlready) {
        if (websocketRef.current && context.profileId) {
          websocketRef.current.subscribe(
            topic.replace("{profileId}", context.profileId),
            { qos: 1 }
          )
          return
        }

        topicSubscriptionsQueue.current.push(topic)
      }
    },
    [context, createLoggingContext, logExceptionWithSentry]
  )

  const removeMessageListener = useCallback((name: string) => {
    // only turn on when debugging listeners
    debug(`WebsocketService: removeMessageListener(${name})`)

    const listener = messageListeners.current.find(propEq("name", name))

    if (!listener) {
      logExceptionWithSentry(
        new Error(`Message handler with name ${name} not found`),
        createLoggingContext({
          service: "WebsocketService",
          method: "removeMessageListener",
        })
      )
      return
    }

    messageListeners.current = messageListeners.current.filter(
      (listener) => listener.name !== name
    )

    // unsubscribe from the topic if no other listener is using it
    const isTopicUsed = messageListeners.current.some(
      (handler) => listener.topic === handler.topic
    )

    if (!isTopicUsed) {
      websocketRef.current?.unsubscribe(listener.topic)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <WebSocketContext.Provider
      value={{
        addMessageListener,
        removeMessageListener,
        connectionStatus,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  )
}

export const useWebSocketService = () => {
  const context = useContext(WebSocketContext)
  if (!context)
    throw new Error("useWebSocket must be used within a WebSocketProvider")
  return context
}
